/** Erro normalizado da API, consumido igualmente por Web e Mobile. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;
  readonly correlationId?: string;

  constructor(params: {
    status: number;
    code?: string;
    message: string;
    details?: unknown;
    correlationId?: string;
  }) {
    super(params.message);
    this.name = 'ApiError';
    this.status = params.status;
    this.code = params.code ?? inferCode(params.status);
    this.details = params.details;
    this.correlationId = params.correlationId;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  get isConflict(): boolean {
    return this.status === 409;
  }

  get isValidation(): boolean {
    return this.status === 422;
  }

  get isServer(): boolean {
    return this.status >= 500;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

export const NETWORK_ERROR_STATUS = 0;
export const TIMEOUT_ERROR_STATUS = -1;

function inferCode(status: number): string {
  switch (status) {
    case NETWORK_ERROR_STATUS:
      return 'NETWORK_ERROR';
    case TIMEOUT_ERROR_STATUS:
      return 'TIMEOUT';
    case 400:
      return 'BAD_REQUEST';
    case 401:
      return 'UNAUTHORIZED';
    case 403:
      return 'FORBIDDEN';
    case 404:
      return 'NOT_FOUND';
    case 409:
      return 'CONFLICT';
    case 422:
      return 'VALIDATION_ERROR';
    case 429:
      return 'RATE_LIMITED';
    default:
      return status >= 500 ? 'SERVER_ERROR' : 'UNKNOWN_ERROR';
  }
}

/** Mensagens padrão em pt-BR, usadas quando a API não devolve uma. */
export const DEFAULT_ERROR_MESSAGES: Record<string, string> = {
  NETWORK_ERROR: 'Sem conexão com o servidor. Verifique sua internet.',
  TIMEOUT: 'A requisição demorou demais para responder.',
  UNAUTHORIZED: 'Sua sessão expirou. Faça login novamente.',
  FORBIDDEN: 'Você não tem permissão para executar esta ação.',
  NOT_FOUND: 'Registro não encontrado.',
  CONFLICT: 'Esta operação conflita com o estado atual do registro.',
  VALIDATION_ERROR: 'Verifique os campos informados.',
  RATE_LIMITED: 'Muitas requisições. Aguarde alguns instantes.',
  SERVER_ERROR: 'Erro inesperado no servidor. Tente novamente.',
  UNKNOWN_ERROR: 'Não foi possível concluir a operação.',
};

export function getErrorMessage(error: unknown): string {
  if (isApiError(error)) {
    return error.message || (DEFAULT_ERROR_MESSAGES[error.code] ?? DEFAULT_ERROR_MESSAGES.UNKNOWN_ERROR!);
  }
  if (error instanceof Error && error.message) return error.message;
  return DEFAULT_ERROR_MESSAGES.UNKNOWN_ERROR!;
}
